<template>
  <div
    v-if="isShowModal"
    class="new-child"
  >
    <span class="new-child__close" @click="closeModal"></span>
    <h2 class="new-child__title capitalize">Please review your {{ camperStudent('singular') }}'s profile</h2>
    <p class="new-child__subtitle">
      Please confirm or update the information below before continuing to the application.
    </p>

    <div
      v-if="isLoading || !isDataFullPrepared"
      class="d-flex center"
    >
      <b-spinner label="Loading"></b-spinner>
    </div>

    <div v-else>
      <RegistrationFormRow
        v-for="field in preparedFields"
        v-show="!field.hidden"
        :key="field.id"
        :fields-data="field"
        :is-disabled-editing="field.readonly"
      />
    </div>

    <p
      v-if="errorMessage"
      class="form__error pt-4"
    >
      {{ errorMessage }}
    </p>

    <p
      v-if="serverMessage"
      class="form__error pt-4"
    >
      {{ serverMessage }}
    </p>

    <button
      class="btn btn--color-dark form__button"
      @click="sendForm"
      :disabled="isLoading || !isDataFullPrepared"
    >
      Confirm &amp; Continue
    </button>
  </div>
</template>

<script>
import { instanceSessionCamperNames } from '@core/mixins/instanceSessionCamperNames'
import store from '@/store'
import { BSpinner } from 'bootstrap-vue'
import RegistrationFormRow from '@/views/Registration/RegistrationFormRow.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'ReviewChildProfileModal',
  components: {
    RegistrationFormRow,
    BSpinner,
  },
  mixins: [instanceSessionCamperNames],
  props: {
    isShowModal: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isDataFullPrepared: false,
    }
  },
  watch: {
    isShowModal(val) {
      if (!val) {
        this.resetModalData()
      }
      if (val) {
        this.setFormsData()
      }
    },
  },
  computed: {
    ...mapGetters({
      preparedFields: 'addNewChild/getPreparedFields',
      hasFileField: 'addNewChild/getHasFileField',
      hasSignatureField: 'addNewChild/getHasSignatureField',
      registrationForm: 'addNewChild/getNewChildForm',
    }),
    isLoading: {
      get() {
        return store.getters['addNewChild/getIsLoading']
      },
      set(val) {
        store.commit('addNewChild/SET_IS_LOADING', val)
      },
    },
    errorMessage: {
      get() {
        return store.getters['addNewChild/getErrorMessage']
      },
      set(val) {
        store.commit('addNewChild/SET_ERROR_MESSAGE', val)
      },
    },
    serverMessage: {
      get() {
        return store.getters['addNewChild/getServerMessage']
      },
      set(val) {
        store.commit('addNewChild/SET_SERVER_MESSAGE', val)
      },
    },
    validTracker: {
      get() {
        return store.getters['addNewChild/getValidTracker']
      },
      set(val) {
        store.commit('addNewChild/SET_VALID_TRACKER', val)
      },
    },
  },
  async created() {
    this.isLoading = true
    try {
      await this.setNewChildForm()
    } catch (error) {
      console.error('Failed to load child form:', error)
      this.errorMessage = 'Unable to load the form. Please try again.'
      this.isLoading = false
      return
    }
    this.isLoading = false

    // Both selectedChild and isShowReviewModal are set in the same synchronous
    // tick in ApplicationSelectChild, so the component is created with
    // isShowModal already true. The watcher only fires for *changes*, so
    // setFormsData() would never be called. Handle that case here.
    if (this.isShowModal) {
      await this.setFormsData()
    }
  },
  methods: {
    async setNewChildForm() {
      await store.dispatch('addNewChild/fetchSchoolNewChildForm', {
        role: 'child',
        query: null,
      })
    },
    async setFormsData() {
      this.isDataFullPrepared = false

      store.commit('addNewChild/SET_FAMILY_NAME', this.user.last_name ?? '')
      store.commit('addNewChild/SET_FORMS_DATA')
      store.commit('addNewChild/CHECK_CONDITIONS')

      try {
        await store.dispatch('addNewChild/fetchUserAttributes', {
          user_id: this.user.id,
        })
        store.commit('addNewChild/SET_FORMS_VALUES_FROM_USER_ATTRIBUTES')
      } catch (error) {
        console.error('Failed to load user attributes:', error)
        this.errorMessage = 'Unable to load the form. Please try again.'
      } finally {
        this.isDataFullPrepared = true
      }
    },
    async validStep() {
      await this.$nextTick()

      const missingRequired = this.preparedFields.filter(
        field =>
          field.required &&
          !['plain_text', 'signature'].includes(field.type) &&
          (field.value == null || field.value.length === 0) &&
          !field.hidden,
      )

      if (missingRequired.length > 0) {
        this.errorMessage = 'Please, fill required fields'
        this.validTracker = true
        return
      }

      const signatureFields = this.preparedFields.filter(
        field => field.type === 'signature' && field.required && !field.hidden,
      )
      if (signatureFields.length > 0) {
        signatureFields.forEach(field => {
          const canvasRef = this.findCanvasRefForField(field)
          if (canvasRef && canvasRef.isEmpty() && !canvasRef.loadedImage) {
            this.errorMessage = 'Please, fill required fields'
            this.validTracker = true
          }
        })
        if (this.validTracker) return
      }

      this.validTracker = false
      this.errorMessage = ''
      this.serverMessage = ''
    },
    async sendForm() {
      this.validTracker = false
      await this.validStep()
      if (this.validTracker) return

      await this.saveChild()
    },
    async saveChild() {
      this.isLoading = true

      let fileFieldsCount = 0
      let avatarFieldsCount = 0

      const queryParams = {
        fields: [],
        form_data: {
          role: 'child',
          family_id: this.user.family_id,
        },
        registration_form_id: this.registrationForm.id,
        type: this.registrationForm.type,
        user_id: this.user.id,
      }

      const filesLinks = []
      const filesNames = []
      const avatarLinks = []
      const avatarNames = []

      if (this.hasFileField) {
        const fileFields = this.preparedFields.filter(
          row => ['avatar', 'file_upload'].includes(row.type) && row.value,
        )
        if (fileFields.length > 0) {
          fileFields.forEach(field => {
            if (Array.isArray(field.value)) {
              Array.from(field.value).forEach(val => {
                if (val instanceof Blob) {
                  filesLinks.push(this.uploadFile(val))
                } else {
                  filesNames.push(val)
                }
              })
            } else {
              if (field.value instanceof Blob) {
                avatarLinks.push(this.uploadFile(field.value, 'avatar'))
              } else {
                avatarNames.push(field.value)
              }
            }
          })
        }
      }

      if (this.hasSignatureField) {
        const signatureFields = this.preparedFields.filter(row => row.type === 'signature')
        signatureFields.forEach(field => {
          const canvasRef = this.findCanvasRefForField(field)
          if (canvasRef && !canvasRef.isEmpty()) {
            field.signature = canvasRef.save()
          }
        })
      }

      const requests = [
        ...filesLinks.map(link => ({ request: link, type: 'file' })),
        ...avatarLinks.map(link => ({ request: link, type: 'avatar' })),
      ]

      Promise.all(requests.map(r => r.request))
        .then(responses =>
          responses.forEach((response, index) => {
            const request = requests[index]
            if (!response.file_path) {
              this.errorMessage = 'Error uploading file'
              throw new Error('Error uploading file')
            }
            if (request.type === 'file') {
              filesNames.push(response.file_path)
            } else if (request.type === 'avatar') {
              avatarNames.push(response.file_path)
            }
          }),
        )
        .then(() => {
          this.preparedFields
            .filter(row => !row.hidden)
            .forEach(row => {
              if (row.type === 'file_upload' && row.value) {
                const from = fileFieldsCount
                const to = fileFieldsCount + row.value.length
                const sliced = filesNames.slice(from, to)
                queryParams.fields.push({ field_id: row.id, files: sliced })
                queryParams.form_data[row.name] = sliced
                fileFieldsCount = to
              } else if (row.type === 'avatar' && row.value) {
                const avatarName = avatarNames[avatarFieldsCount]
                queryParams.fields.push({ field_id: row.id, files: [avatarName] })
                queryParams.form_data[row.name] = avatarName
                avatarFieldsCount++
              } else if (row.type === 'signature' && row.signature) {
                queryParams.fields.push({ field_id: row.id, result: row.signature })
                queryParams.form_data[row.name] = row.signature
              } else {
                queryParams.fields.push({ field_id: row.id, result: row.value })
                queryParams.form_data[row.name] = row.value
              }
            })

          store
            .dispatch('addNewChild/createOrUpdateChild', queryParams)
            .then(() => {
              // Signal the parent that the review is done so it can proceed
              // to the application form.
              this.$emit('reviewed')
              this.closeModal()
            })
            .catch(error => {
              if (error.response?.status === 422) {
                const validationErrors = Object.values(error.response.data.errors)
                this.serverMessage = validationErrors.flat().join(', ')
              } else if (error.response?.status === 500) {
                this.serverMessage =
                  'Something went wrong. Please contact Administration for details.'
              }
            })
            .finally(() => {
              this.isLoading = false
            })
        })
        .catch(error => {
          // Catches Promise.all rejections (a file/avatar upload request
          // failed or never resolved) and the missing-file_path guard above.
          // Without this, those paths never reach the inner dispatch/.finally()
          // and isLoading was left stuck true forever.
          console.error('Failed to upload child files:', error)
          if (!this.errorMessage) {
            this.errorMessage = 'Something went wrong while uploading files. Please try again.'
          }
          this.isLoading = false
        })
    },
    findCanvasRefForField(field) {
      if (this.$refs[field.ref]) {
        return this.$refs[field.ref][0]?.$refs[`signature_${field.ref}`]?.$refs[field.ref]
      }
      return null
    },
    async uploadFile(file) {
      const formData = new FormData()
      formData.append('file', file)
      return store.dispatch('files/uploadFiles', {
        name: `review-child-modal-files-${new Date().getTime()}`,
        file: formData,
      })
    },
    resetModalData() {
      store.commit('addNewChild/RESET_DATA')
      this.isDataFullPrepared = false
    },
    closeModal() {
      this.resetModalData()
      this.$emit('closeModal')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../../assets/scss/colors/colors';

.new-child {
  position: fixed;
  border-radius: 10px;
  padding: 50px;
  width: 500px;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  z-index: 20;
  box-shadow: 0px 0px 0px 9999px rgba(0, 0, 0, 0.5);
  max-height: 90vh;
  overflow-y: scroll;
  transition: 0.5s;

  &::-webkit-scrollbar { width: 6px; height: 90%; }
  &::-webkit-scrollbar-button { height: 10px; }
  &::-webkit-scrollbar-track { box-shadow: inset 0 0 5px #fff; border-radius: 10px; }
  &::-webkit-scrollbar-thumb { background: $primary; border-radius: 10px; }
  &::-webkit-scrollbar-thumb:hover { background: #69182d; }

  &__close {
    position: absolute;
    display: flex;
    align-items: center;
    top: 20px;
    right: 20px;
    width: 15px;
    height: 15px;
    cursor: pointer;

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      left: 6px;
      width: 1px;
      height: 15px;
      background: #000;
      transform: rotate(45deg);
    }
    &::after { transform: rotate(-45deg); }
  }

  &__title {
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 10px;
  }

  &__subtitle {
    font-size: 14px;
    text-align: center;
    color: #666;
    margin-bottom: 20px;
  }
}

@media screen and (max-width: 767px) {
  .new-child {
    position: absolute;
    width: 90%;
    padding: 20px;
    left: 5%;
    top: 20px;
    transform: none;
    max-height: calc(100vh - 40px);
    overflow-y: auto;
  }
}
</style>

